import React, { useState, useEffect } from 'react';
import { useRecipes } from '@/contexts/RecipesContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Spinner } from '@/components/Spinner';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import ProtectedRoute from '@/components/ProtectedRoute';
import { PlusCircle, Search, Edit, Trash2, Upload, Download } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { exportRecipesToCsv } from '@/utils/csvUtils';

const AdminRecipes = () => {
  const { recipes, loading, deleteRecipe } = useRecipes();
  const [searchTerm, setSearchTerm] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedRecipes, setSelectedRecipes] = useState<string[]>([]);
  const [deletionSuccess, setDeletionSuccess] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deleteProgress, setDeleteProgress] = useState({ current: 0, total: 0 });

  const filteredRecipes = recipes.filter((recipe) =>
    recipe.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    if (deletionSuccess) {
      setSelectedRecipes([]);
      setDeletionSuccess(false);
    }
  }, [recipes, deletionSuccess]);

  const handleSelectAll = () => {
    if (selectedRecipes.length === filteredRecipes.length) {
      setSelectedRecipes([]);
    } else {
      setSelectedRecipes(filteredRecipes.map(recipe => recipe.id));
    }
  };

  const handleSelectRecipe = (id: string) => {
    setSelectedRecipes(prev => 
      prev.includes(id) 
        ? prev.filter(recipeId => recipeId !== id)
        : [...prev, id]
    );
  };

  const handleDeleteSelected = async () => {
    setIsDeleting(true);
    setDeleteProgress({ current: 0, total: selectedRecipes.length });
    
    try {
      console.log(`Starting bulk deletion of ${selectedRecipes.length} recipes:`, selectedRecipes);
      
      let successCount = 0;
      let failCount = 0;
      
      for (let i = 0; i < selectedRecipes.length; i++) {
        const id = selectedRecipes[i];
        setDeleteProgress({ current: i + 1, total: selectedRecipes.length });
        
        try {
          console.log(`Deleting recipe ${i + 1}/${selectedRecipes.length} with ID: ${id}`);
          await deleteRecipe(id);
          successCount++;
        } catch (error) {
          console.error(`Error deleting recipe ID ${id}:`, error);
          failCount++;
        }
      }
      
      if (failCount > 0) {
        toast.error(`Error al eliminar ${failCount} recetas`);
      }
      
      if (successCount > 0) {
        toast.success(`${successCount} recetas eliminadas correctamente`);
      }
      
      setDeletionSuccess(true);
      setIsDeleteDialogOpen(false);
    } catch (error) {
      console.error('Error in bulk delete operation:', error);
      toast.error('Error al eliminar las recetas seleccionadas');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteRecipe = async (id: string) => {
    setIsDeleting(true);
    try {
      console.log(`Attempting to delete single recipe with ID: ${id}`);
      await deleteRecipe(id);
      
      setSelectedRecipes(prev => prev.filter(recipeId => recipeId !== id));
      setDeletionSuccess(true);
      toast.success('Receta eliminada correctamente');
    } catch (error) {
      console.error(`Error deleting recipe with ID ${id}:`, error);
      toast.error('Error al eliminar la receta');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleExportCsv = () => {
    const csvContent = exportRecipesToCsv(recipes);
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `recetas-${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success('Recetas exportadas correctamente');
  };

  return (
    <ProtectedRoute requireAdmin={true}>
      <div className="flex flex-col min-h-screen">
        <Navbar />

        <main className="flex-grow py-12 px-4 bg-gray-50">
          <div className="container mx-auto">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
              <h1 className="text-3xl md:text-4xl font-bold text-vegana-primary">Administrar Recetas</h1>

              <div className="flex flex-col md:flex-row gap-2">
                {selectedRecipes.length > 0 && (
                  <Button 
                    variant="destructive" 
                    className="w-full md:w-auto"
                    onClick={() => setIsDeleteDialogOpen(true)}
                  >
                    <Trash2 size={16} className="mr-2" />
                    Eliminar ({selectedRecipes.length})
                  </Button>
                )}
                <Button 
                  variant="outline" 
                  className="w-full md:w-auto"
                  onClick={handleExportCsv}
                >
                  <Download size={16} className="mr-2" />
                  Exportar CSV
                </Button>
                <Link to="/admin/importar-recetas">
                  <Button variant="outline" className="w-full md:w-auto border-vegana-primary text-vegana-primary hover:bg-vegana-primary/10">
                    <Upload size={16} className="mr-2" />
                    Importar Recetas
                  </Button>
                </Link>
                <Link to="/admin/recetas/nueva">
                  <Button className="w-full md:w-auto bg-vegana-primary hover:bg-vegana-primary/90">
                    <PlusCircle size={16} className="mr-2" />
                    Añadir Receta
                  </Button>
                </Link>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm">
              <div className="relative mb-6">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                <Input
                  placeholder="Buscar recetas..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>

              {loading ? (
                <div className="flex justify-center py-12">
                  <Spinner size="lg" />
                </div>
              ) : filteredRecipes.length > 0 ? (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[50px]">
                          <Checkbox
                            checked={selectedRecipes.length === filteredRecipes.length && filteredRecipes.length > 0}
                            onCheckedChange={handleSelectAll}
                            aria-label="Seleccionar todas"
                          />
                        </TableHead>
                        <TableHead>Título</TableHead>
                        <TableHead>Calorías</TableHead>
                        <TableHead>Tiempo Total</TableHead>
                        <TableHead>Porciones</TableHead>
                        <TableHead className="text-right">Acciones</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredRecipes.map((recipe) => (
                        <TableRow key={recipe.id}>
                          <TableCell>
                            <Checkbox
                              checked={selectedRecipes.includes(recipe.id)}
                              onCheckedChange={() => handleSelectRecipe(recipe.id)}
                              aria-label={`Seleccionar ${recipe.title}`}
                            />
                          </TableCell>
                          <TableCell>
                            <div className="font-medium">{recipe.title}</div>
                            <div className="text-sm text-gray-500 truncate max-w-xs">{recipe.description}</div>
                          </TableCell>
                          <TableCell>{recipe.calories} kcal</TableCell>
                          <TableCell>{recipe.prepTime + recipe.cookTime} min</TableCell>
                          <TableCell>{recipe.servings}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Link to={`/admin/recetas/editar/${recipe.id}`}>
                                <Button variant="outline" size="sm">
                                  <Edit size={14} />
                                </Button>
                              </Link>
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button variant="outline" size="sm" className="border-red-500 text-red-500 hover:bg-red-50">
                                    <Trash2 size={14} />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>¿Eliminar esta receta?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      Esta acción no se puede deshacer. Esto eliminará permanentemente la receta.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={() => handleDeleteRecipe(recipe.id)}
                                      className="bg-red-500 hover:bg-red-600"
                                    >
                                      {isDeleting ? 'Eliminando...' : 'Eliminar'}
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="text-center py-12">
                  <h3 className="text-xl font-medium text-gray-600">
                    No se encontraron recetas
                  </h3>
                  <p className="text-gray-500 mt-2">
                    Intenta con otros términos de búsqueda o añade una nueva receta
                  </p>
                </div>
              )}
            </div>
          </div>
        </main>

        <Footer />
      </div>

      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>¿Eliminar las recetas seleccionadas?</DialogTitle>
            <DialogDescription>
              Esta acción eliminará {selectedRecipes.length} recetas permanentemente.
            </DialogDescription>
          </DialogHeader>
          
          {isDeleting && (
            <div className="my-4">
              <div className="flex justify-between mb-2">
                <span>Eliminando recetas...</span>
                <span>{deleteProgress.current} de {deleteProgress.total}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div 
                  className="bg-red-600 h-2.5 rounded-full" 
                  style={{ width: `${(deleteProgress.current / deleteProgress.total) * 100}%` }}
                ></div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)} disabled={isDeleting}>
              Cancelar
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteSelected} 
              disabled={isDeleting}
            >
              {isDeleting ? 'Eliminando...' : 'Eliminar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ProtectedRoute>
  );
};

export default AdminRecipes;
